'use client'

import useSWR from 'swr'
import * as Yup from 'yup'
import { useState } from 'react'
import { useFormik } from 'formik'
import { BiCheck } from 'react-icons/bi'
import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'

import { fetcher, postFeedback } from '@/api/functions'
import { ApiRoutes } from '@/api/routes'
import { IPreferencesData } from '@/types'
import PhoneIcon from '@/components/icons/Phone'
import LetterIcon from '@/components/icons/Letter'
import LocationIcon from '@/components/icons/Location'

const Form = () => {
  const t = useTranslations('ContactSection')
  const { locale } = useParams()

  const [disabled, setDisabled] = useState<boolean>(false)
  const [status, setStatus] = useState<string>(t('order'))
  const { data } = useSWR<IPreferencesData[]>(
    ApiRoutes.settings.contacts + '&lang=' + locale,
    fetcher
  )

  const validationSchema = Yup.object({
    name: Yup.string().required(t('enterName')),
    phone: Yup.string()
      .matches(/^\+[\d\s]+$/, t('formatNumber'))
      .required(t('enterNumber')),
    comment: Yup.string(),
    is_agree: Yup.boolean().oneOf([true], t('mustConfirm')),
  })

  const address = data?.find((val) => val.key === 'address')
  const email = data?.find((val) => val.key === 'email')
  const phone = data?.find((val) => val.key === 'phone')

  const { values, errors, resetForm, handleBlur, handleChange, handleSubmit, touched } = useFormik({
    initialValues: {
      name: '',
      phone: '',
      comment: '',
      is_agree: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      setStatus('Submitting...')
      setDisabled(true)

      try {
        await postFeedback(values)

        setStatus('Your message is sent!')
        resetForm()
      } catch (e) {
        setStatus('Failed!')
      }

      setTimeout(() => {
        setDisabled(false)
        setStatus(t('order'))
      }, 3000)
    },
  })

  return (
    <div className="bg-white px-4 py-[30px] md:py-[100px]" id="contact">
      <div className="container mx-auto grid grid-cols-2 gap-5">
        <div className="col-span-2 md:col-span-1">
          <h2 className="mb-1 md:mb-4">{t('title')}</h2>
          <p className="mb-8 w-[80%] font-medium text-secondary-dark md:mb-[50px]">
            {t('description')}
          </p>

          <div className="flex flex-col gap-5 text-xs md:text-base">
            {phone ? (
              <span className="flex items-center gap-3">
                <span className="text-[24px] text-primary">
                  <PhoneIcon />
                </span>
                <a
                  href={phone?.value}
                  aria-label={phone?.value}
                  className="font-medium text-secondary-darkest"
                >
                  {phone?.value}
                </a>
              </span>
            ) : null}
            {email ? (
              <span className="flex items-center gap-3">
                <span className="text-[24px] text-primary">
                  <LetterIcon />
                </span>
                <a
                  href={'mailto:' + email?.value}
                  aria-label={email?.value}
                  className="font-medium text-secondary-darkest"
                >
                  {email?.value}
                </a>
              </span>
            ) : null}
            {address ? (
              <span className="flex items-center gap-3">
                <span className="text-[24px] text-primary">
                  <LocationIcon />
                </span>
                <a
                  href={address?.value}
                  aria-label={address?.value}
                  className="font-medium text-secondary-darkest"
                >
                  {address?.value}
                </a>
              </span>
            ) : null}
          </div>
        </div>
        <div className="col-span-2 md:col-span-1">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4 text-xs md:text-base">
            <input
              type="text"
              name="name"
              id="name"
              placeholder={t('name')}
              className="form-input"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
            />
            {touched.name && errors.name ? <div className="text-red-500">{errors.name}</div> : null}
            <input
              type="text"
              name="phone"
              id="phone"
              placeholder={t('number')}
              className="form-input"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.phone}
            />
            {touched.phone && errors.phone ? (
              <div className="text-red-500">{errors.phone}</div>
            ) : null}
            <textarea
              placeholder={t('comment')}
              className="form-input max-h-[200px] resize-none"
              name="comment"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.comment}
            />
            <label htmlFor="is_agree" className="flex cursor-pointer items-center gap-2">
              <div
                className={`flex h-[20px] w-[20px] items-center justify-center rounded-md border-2 ${
                  values.is_agree ? 'border-primary bg-primary text-white' : 'border-[#C1D8CF]'
                }`}
              >
                <BiCheck className={`duration-200 ${values.is_agree ? 'scale-100' : 'scale-0'}`} />
              </div>
              <input
                type="checkbox"
                name="is_agree"
                id="is_agree"
                hidden
                checked={values.is_agree}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <span className="font-medium text-secondary">{t('confirmation')}</span>
            </label>
            {touched.is_agree && errors.is_agree ? (
              <div className="text-red-500">{errors.is_agree}</div>
            ) : null}
            <button
              type="submit"
              className="btn-primary-light disabled:bg-gray-400"
              disabled={disabled}
              aria-label={t('order')}
            >
              {status}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Form
